import { orderBy } from 'lodash';
import {
  Device,
  DomainRole,
  Location,
  LocationACL,
  ResourceACL,
  ResourceGrant,
  ResourceProfile,
} from '@raydiant/api-client-js';
import { BatchDevice } from '../../selectors/v2/devices';
import { mapScreensFilterQuery } from './ScreensFilter/utils';
import { getDeviceConnectionStatus } from '../../utilities';

export interface DeviceResultsByLocation {
  [locationId: string]: {
    location: Location;
    devices: BatchDevice[];
  };
}

export const sortLocations = (data: Location[]) => {
  return orderBy(data, ['location.name'], ['asc']);
};

export const getQueryFilteredLocations = (
  locations: Location[],
  query?: string,
) => {
  const searchQuery = mapScreensFilterQuery(query || '');
  const selectedLocationIds = searchQuery.locations?.locationIds || [];

  if (selectedLocationIds.length === 0) return locations;

  const filterdLocations = locations.filter((location) =>
    selectedLocationIds.includes(location.id),
  );

  return sortLocations(filterdLocations);
};

export const getDevicesByLocationId = (
  locations: Location[],
  devices: BatchDevice[],
) => {
  const deviceResults: DeviceResultsByLocation = {};
  locations.forEach((location: Location) => {
    deviceResults[location.id] = {
      location,
      devices: [],
    };
  });

  devices.forEach((device) => {
    const location = device.location;
    if (!location) return;

    const locationId = location.id;

    if (deviceResults[locationId]) {
      deviceResults[locationId].devices.push(device);
    } else {
      deviceResults[locationId] = {
        location,
        devices: [device],
      };
    }
  });

  return deviceResults;
};

export const getLimitedAccessUsersWithScreens = (devices: Device[]) => {
  type ProfileDeviceStruct = {
    [key: string]: {
      profile: ResourceProfile;
      devices: Device[];
    };
  };

  const profileDevice: ProfileDeviceStruct = {};

  devices.forEach((device) => {
    device.resource?.r?.resourceACLs.forEach((acl) => {
      const profileId = acl?.r?.grantProfile?.id || '';
      if (profileDevice.hasOwnProperty(profileId)) {
        profileDevice[profileId] = {
          ...profileDevice[profileId],
          devices: [...profileDevice[profileId].devices, device],
        };
      } else {
        if (acl.r.grantProfile === null) return;
        profileDevice[profileId] = {
          profile: acl.r.grantProfile,
          devices: [device],
        };
      }
    });
  });

  return Object.values(profileDevice);
};

const avatarColorSet = [
  {
    backgroundColor: 'bg-[#ffebec]',
    color: 'text-[#c22b33]',
  },
  {
    backgroundColor: 'bg-[#e5fbf8]',
    color: 'text-[#058a82]',
  },
  {
    backgroundColor: 'bg-[#f0eafa]',
    color: 'text-[#6e33c9]',
  },
  {
    backgroundColor: 'bg-[#e5ecfb]',
    color: 'text-[#0530a6]',
  },
  {
    backgroundColor: 'bg-[#ffedea]',
    color: 'text-[#d43b1f]',
  },
  {
    backgroundColor: 'bg-[#e7f7ed]',
    color: 'text-[#0f7a33]',
  },
];

export const getAvatarStyle = (index: number) => {
  const backgroundColorIndex = index % 6;
  switch (backgroundColorIndex) {
    case 0:
      return avatarColorSet[0];
    case 1:
      return avatarColorSet[1];
    case 2:
      return avatarColorSet[2];
    case 3:
      return avatarColorSet[3];
    case 4:
      return avatarColorSet[4];
    case 5:
      return avatarColorSet[5];

    default:
      return avatarColorSet[0];
  }
};

export const getInitialsFromName = (name: string) => {
  const [firstName, lastName] = name.split(' ');

  let initials = '';

  if (firstName) {
    initials += firstName.charAt(0);
  }

  if (lastName) {
    initials += lastName.charAt(0);
  }

  return initials.toUpperCase();
};

export const getConnectionStatusFilteredDevice = (
  devices: BatchDevice[],
  query: string,
  lastLoadedDate: string,
): BatchDevice[] => {
  const filterDeviceByConnectionStatusQuery = devices.filter((device) => {
    if (!query) return true;
    const { isOnline } = getDeviceConnectionStatus(device, lastLoadedDate);
    if (query.includes('connection_status=online')) {
      return isOnline;
    }
    if (query.includes('connection_status=offline')) {
      return !isOnline;
    }
    return true;
  });
  return filterDeviceByConnectionStatusQuery;
};

export const getResourceACLToLocationACL = (
  resourceACL: ResourceACL,
): LocationACL => {
  const locationACL = {
    id: resourceACL.id,
    grantProfile: {
      id: resourceACL.r.grantProfile?.id || '',
      email: resourceACL.r.grantProfile?.email || '',
      name: resourceACL.r.grantProfile?.name || '',
      thumbnailUrl: resourceACL.r.grantProfile?.thumbnailUrl,
      domainId: resourceACL.r.grantProfile?.domainId || '',
      domainRole: resourceACL.r.grantProfile?.domainRole as DomainRole,
    },
    grantDomainId: resourceACL.grantDomainId,
    grantScope: resourceACL.grantScope,
    grantFolderPath: resourceACL.grantFolderPath,
    readGrant: resourceACL.readGrant,
    updateGrant: resourceACL.updateGrant,
    deleteGrant: resourceACL.deleteGrant,
    customAccessGrants: resourceACL.customAccessGrants,
    shareGrant: resourceACL.shareGrant,
    fullGrant: resourceACL.fullGrant,
  };
  return locationACL;
};

export const getLocationACLsToDevice = (
  device: Device,
  acls: LocationACL[],
): Device => {
  const updatedResourceACLs = acls.map((item) => ({
    id: item.id,
    resourceId: device.resource.id,
    profileId: device.resource.profile.id,
    grantProfileId: item.grantProfile?.id || '',
    grantDomainId: item.grantDomainId,
    grantScope: item.grantScope,
    grantFolderPath: item.grantFolderPath,
    readGrant: item.readGrant,
    updateGrant: item.updateGrant,
    shareGrant: item.shareGrant,
    deleteGrant: item.deleteGrant,
    customAccessGrants: item.customAccessGrants,
    fullGrant: item.fullGrant,
    createdAt: '',
    updatedAt: '',
    r: {
      profile: device.resource.profile,
      grantProfile: {
        ...item.grantProfile,
        thumbnailUrl: '',
        domainId: '',
      },
    },
  }));

  return {
    ...device,
    resource: {
      ...device.resource,
      r: {
        ...device.resource.r,
        resourceACLs: updatedResourceACLs,
      },
    },
  };
};

export const selectItemToGrants = (grant: ResourceGrant): ResourceGrant[] => {
  switch (grant) {
    case 'delete':
      return ['read', 'update', 'publish', 'share', 'delete'];
    case 'share':
      return ['read', 'update', 'publish', 'share'];
    case 'publish':
      return ['read', 'update', 'publish'];
    case 'update':
      return ['read', 'update'];
    case 'read':
      return ['read'];
    default:
      return ['read'];
  }
};

export const calculateTrialDaysLeft = (
  createdAt: string,
  trialDays: number = 30,
): number => {
  if (!createdAt) return 0;

  const createdDate = new Date(createdAt);
  const currentDate = new Date();
  const trialEndDate = new Date(createdDate);
  trialEndDate.setDate(createdDate.getDate() + trialDays);

  const timeDiff = trialEndDate.getTime() - currentDate.getTime();
  const daysLeft = Math.floor(timeDiff / (1000 * 3600 * 24));

  return Math.max(0, daysLeft);
};

export const getTrialText = (daysLeft: number) => {
  if (daysLeft === 0) return 'Expired';
  if (daysLeft === 1) return '1 Day';
  return `${daysLeft} Days`;
};
